<template>
  <div v-if="loaded" class="posts-container">
    <div
      class="no-posts-admin"
      v-if="userIsAdmin && clubData.news.length === 0"
    >
      <p>
        Create a post with news for members to read. <br />
        You can add photos and documents to all posts.
      </p>
      <button
        class="create-post-button"
        v-if="userIsAdmin"
        @click="$router.push({ name: 'organization-post-news' })"
      >
        <Add color="white" />
        <p><b>Create post</b></p>
      </button>
    </div>
    <div class="no-posts" v-else-if="clubData.news.length === 0">
      <p>This club hasn’t posted anything yet.</p>
    </div>
    <template v-else>
      <button
        class="create-post-button"
        v-if="userIsAdmin"
        @click="$router.push({ name: 'organization-post-news' })"
      >
        <Add color="white" />
        <p><b>Create post</b></p>
      </button>

      <section class="news-post" v-for="news in clubData.news">
        <carousel :autoplay="true" :autoplayTimeout="10000" :perPage="1">
          <slide
            v-for="(image, imageIndex) in newsImages(news.attachments)"
            :key="imageIndex"
          >
            <img
              class="news-slider-image"
              :src="contentImageThumbnailUrl(image.url)"
              alt="News image"
              @click="
                activeNewsSection = news
                index = imageIndex
              "
            />
          </slide>
        </carousel>
        <div class="news-wrapper">
          <p class="unpublished-label" v-if="!news.published">
            <Hide color="sunset" /><b>Unpublished</b>
          </p>
          <div class="news-header">
            <UserProfileAvatar
              size="L"
              :profileImageUrl="news.createdBy.profile.profileImageUrl"
              :initials="
                news.createdBy.firstName.charAt(0) +
                news.createdBy.lastName.charAt(0)
              "
            />
            <div class="news-header-info-wrapper">
              <p class="poster">
                <b
                  >{{ news.createdBy.firstName }}
                  {{ news.createdBy.lastName }}</b
                >
              </p>
              <p class="post-date">
                {{ dateFormatter(news.createdAt, 'D MMM YYYY') }}
              </p>
              <template v-if="news.audience === 'MEMBER'">
                <p class="round-separator"></p>
                <p class="members-only-label">Members only</p>
              </template>
            </div>
            <ActionMenu
              v-if="userIsAdmin"
              :items="newsOptions(news)"
              @delete="postToDelete = news"
              @togglePublish="togglePublish(news)"
              @manageAttachments="
                $router.push({
                  name: 'organization-manage-post-attachments',
                  params: { postId: news.id },
                })
              "
              @edit="
                $router.push({
                  name: 'organization-edit-news',
                  params: { postId: news.id },
                })
              "
            />
          </div>
          <div class="news-title">
            <h3>{{ news.title }}</h3>
          </div>

          <vue-markdown
            class="news-body clubs-markdown"
            :source="news.body"
          ></vue-markdown>

          <div class="news-attachments" v-if="hasAttachments(news.attachments)">
            <a
              :href="attachment.url"
              class="attachment"
              v-for="attachment in newsAttachments(news.attachments)"
            >
              <p>
                <b>
                  {{ getFileNameFromUrl(attachment.url) }}
                </b>
              </p>
              <Download2 />
            </a>
          </div>
        </div>
      </section>
      <infinite-loading @infinite="loadMorePosts" spinner="spiral">
        <div slot="no-more">
          Congrats! You’ve reached our very first post 🐣
        </div>
        <div slot="no-results"></div>
      </infinite-loading>
      <CoolLightBox
        :items="lightBoxImages"
        :index="index"
        @close="
          activeNewsSection = null
          index = null
        "
      >
      </CoolLightBox>
    </template>

    <sweet-modal
      :enable-mobile-fullscreen="false"
      blocking
      ref="deletepost"
      hide-close-button
    >
      <DeletePost
        @close="postToDelete = null"
        @delete="deletePost(postToDelete)"
      />
    </sweet-modal>
  </div>
</template>

<script>
import dateFormats from '@/mixins/dateFormats.js'
import VueMarkdown from 'vue-markdown'
import CoolLightBox from 'vue-cool-lightbox'
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css'
import Add from '@/assets/icons/Add.vue'
import Hide from '@/assets/icons/Hide.vue'
import UserProfileAvatar from '@/components/UIElements/UserProfileAvatar'
import Download2 from '@/assets/icons/Download2.vue'
import { Carousel, Slide } from 'vue-carousel'
import ActionMenu from '@/components/ActionMenu.vue'
import DeletePost from '@/pages/clubs/modals/DeletePost.vue'
import InfiniteLoading from 'vue-infinite-loading'

export default {
  name: 'PublicClubNews',
  props: ['me', 'userIsAdmin'],
  mixins: [dateFormats],
  data() {
    return {
      loaded: false,
      clubData: null,
      index: null,
      activeNewsSection: null,
      postToDelete: null,
    }
  },
  components: {
    VueMarkdown,
    CoolLightBox,
    Add,
    UserProfileAvatar,
    Download2,
    Carousel,
    Slide,
    ActionMenu,
    Hide,
    DeletePost,
    InfiniteLoading,
  },
  watch: {
    postToDelete: function (newVal) {
      if (newVal !== null) {
        this.$refs.deletepost.open()
      } else {
        this.$refs.deletepost.close()
      }
    },
  },
  computed: {
    lightBoxImages() {
      const currentSection = this.clubData.news.find(
        (news) => news.id === this.activeNewsSection?.id
      )
      return currentSection
        ? currentSection.attachments
            .filter((news) => news.documentType === 'IMAGE')
            .map((image) => image.url)
        : []
    },
  },
  methods: {
    async deletePost(post) {
      try {
        const deletePost = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            mutation {
              DeleteOrganizationContent(contentId:"${post.id}")
            }
          `,
          },
        })

        if (deletePost.data.errors) {
          throw deletePost.data.errors[0]
        }
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Post deleted',
          type: 'success',
        })
        this.$refs.deletepost.close()
        this.loadData()
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: err.message || 'Error deletin gpost',
          type: 'error',
        })
      }
    },
    async togglePublish(section) {
      try {
        const updatedSection = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            mutation UpdateOrganizationContent($UpdateOrganizationContent: UpdateOrganizationContentInput!){
              UpdateOrganizationContent(contentId:"${section.id}", input: $UpdateOrganizationContent){
                id
              }
            }
          `,
            variables: {
              UpdateOrganizationContent: {
                published: !section.published,
              },
            },
          },
        })

        if (updatedSection.data.errors) {
          throw updatedSection.data.errors[0]
        }

        section.published = !section.published

        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Section updated',
          type: 'success',
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error updating section',
          type: 'error',
        })
      }
    },
    newsOptions(news) {
      const menuItems = []

      menuItems.push({
        title: 'Edit text',
        action: 'edit',
        disabled: false,
        textColor: '',
        subItems: [],
        confirmationRequired: false,
        separator: null,
      })
      menuItems.push({
        title: 'Edit files',
        action: 'manageAttachments',
        disabled: false,
        textColor: '',
        subItems: [],
        confirmationRequired: false,
        separator: null,
      })

      menuItems.push({
        title: news.published ? 'Unpublish' : 'Publish',
        action: 'togglePublish',
        disabled: false,
        textColor: '',
        subItems: [],
        confirmationRequired: false,
        separator: null,
      })
      menuItems.push({
        title: 'Delete',
        action: 'delete',
        disabled: false,
        textColor: '#FF0064',
        subItems: [],
        confirmationRequired: false,
        separator: null,
      })
      return menuItems
    },
    getFileNameFromUrl(url) {
      const fileName = url
        .substring(url.lastIndexOf('/') + 1)
        .replace(/\.[^/.]+$/, '')
      // First decode the URL encoding
      const decoded = decodeURIComponent(fileName)
      // Then convert from ISO-8859-1 to UTF-8
      return decodeURIComponent(
        decoded
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join('')
      )
    },
    newsImages(attachments) {
      return attachments.filter((a) => a.documentType === 'IMAGE')
    },
    newsAttachments(attachments) {
      return attachments.filter((a) => a.documentType === 'DOCUMENT')
    },
    hasAttachments(attachments) {
      return attachments.some((a) => a.documentType === 'DOCUMENT')
    },

    contentImageThumbnailUrl(imageBaseUrl) {
      const imageSizeSuffix = '_x512.'

      const url = imageBaseUrl
      const fileName = url.substring(url.lastIndexOf('/') + 1)
      const newFileName = fileName.replace('.', imageSizeSuffix)
      const newURL = url.replace(fileName, newFileName)

      return newURL
    },
    async loadMorePosts($state) {
      const slug = this.$route.params.slug

      const club = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
          {
            organization(slug:"${slug}"){
              news(first:5, skip:${this.clubData.news.length}){
                id
                published
                audience
                title
                position
                body
                attachments{
                  id
                  documentType
                  url
                }
                createdAt
                createdBy{
                  id
                  firstName
                  lastName
                  profile{
                    profileImageUrl
                  }
                }
                updatedAt
                updatedBy{
                  id
                  firstName
                  lastName
                }
              }
            }
          }
          `,
        },
      })

      if (club.data.data.organization.news.length > 0) {
        this.clubData.news.push(...club.data.data.organization.news)
        $state.loaded()
      } else {
        $state.complete()
      }
    },
    async loadData() {
      const slug = this.$route.params.slug

      const club = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
          {
            organization(slug:"${slug}"){
              news(first:5, skip:0){
                id
                published
                audience
                title
                position
                body
                attachments{
                  id
                  documentType
                  url
                }
                createdAt
                createdBy{
                  id
                  firstName
                  lastName
                  profile{
                    profileImageUrl
                  }
                }
                updatedAt
                updatedBy{
                  id
                  firstName
                  lastName
                }
              }
            }
          }
          `,
        },
      })

      this.clubData = club.data.data.organization
      this.loaded = true
    },
  },
  beforeMount() {
    this.loadData()
  },
  beforeCreate() {},
  destroyed() {},
}
</script>
<style lang="scss">
.infinite-status-prompt {
  font-size: 0.875rem;
  color: $strom;
  margin-top: 32px;
}
.news-post {
  .VueCarousel {
    overflow: hidden;
  }

  .VueCarousel-pagination {
    background-color: white;
  }
}

.clubs-markdown {
  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5rem;
    @include Gilroy-Regular;
  }
  strong {
    @include Gilroy-Bold;
  }
}

@media (min-width: 1200px) {
  .clubs-markdown {
    p {
      font-size: 1.125rem;
      line-height: 1.625rem;
    }
  }
}
</style>
<style lang="scss" scoped>
.no-posts-admin,
.no-posts {
  margin-top: 64px;
  text-align: center;
  p {
    font-size: 0.875rem;
    line-height: 1.5rem;
  }
  .create-post-button {
    margin-top: 24px;
  }
}
.news-wrapper {
  margin-top: 24px;

  &.unpublished {
    color: $strom !important;
  }
}
.unpublished-label {
  margin-bottom: 18px;
  background: #fff0ec;
  border-radius: 4px;
  font-size: 0.875rem;
  padding: 6px 8px;
  display: inline-flex;
  align-items: center;
  color: $sunset;
  letter-spacing: 0.05rem;

  svg {
    margin-right: 6px;
    width: 20px;
  }
}
.no-posts {
  font-size: 1rem;
}
.admin-no-posts {
  margin-top: 32px;
  text-align: center;
  font-size: 1rem;
}
.news-slider-image {
  cursor: pointer;
  max-width: 100%;
  height: auto;
  max-height: 512px;
  object-fit: contain;
  border-radius: 10px;
}
.edit-news-button {
  margin-top: 20px;
}
.news-attachments {
  margin-top: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5%;
}

.attachment {
  background-color: #f3fcfc;
  border: 1px solid $ocean;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  padding: 0 12px;
  border-radius: 20px;
  height: 40px;
  max-width: 45%;
  transition: all ease 0.3s;

  &:hover {
    text-decoration: none;
    background: #d9f4f5;
  }

  &:nth-child(n + 3) {
    margin-top: 12px;
  }
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
    color: $midnight;
  }
  svg {
    margin-left: 8px;
    width: 20px;
    height: 20px;
  }
}
.news-post {
  margin-top: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid $fog;
}
.news-title,
.news-body {
  margin-top: 18px;
}

.news-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.news-header {
  display: flex;
  align-items: stretch;
  .action-menu {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
}
.news-header-info-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-left: 10px;
}

.poster {
  font-size: 1rem;
  width: 100%;
  line-height: 1.125rem;
}

.post-date {
  color: $strom;
  font-size: 0.875rem;
  line-height: 0.875rem;
  line-height: 1rem;
}

.members-only-label {
  color: $gold;
}

@import '@/styles/Typography';
.posts-container {
  padding: 0 15px;
}
.create-post-button {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $twilight;
  border: 0;
  border-radius: 6px;
  width: 100%;

  &:hover {
    background: $twilightshade;
  }
  p {
    color: white;
  }
  svg {
    margin-right: 8px;
    width: 20px;
    height: 20px;
  }
}

@media (min-width: 600px) {
  .unpublished-label {
    margin-bottom: 32px;
    font-size: 1rem;
    svg {
      width: 24px;
    }
  }
  .posts-container {
    padding: 0;
  }
  .create-post-button {
    width: auto;
    margin-left: auto;
  }

  .news-post {
    display: grid;
    grid-template-columns: 2fr 5fr;
    gap: 8%;
  }
}

@media (min-width: 1200px) {
  .no-posts-admin,
  .no-posts {
    margin-top: 128px;
    p {
      font-size: 1.125rem;
      line-height: 1.625rem;
    }
    .create-post-button {
      p {
        font-size: 1rem;
      }

      margin: 48px auto 0 auto;
    }
  }
  .news-wrapper {
    margin-top: 0;
  }
  .no-posts {
    font-size: 1.125rem;
  }
  .admin-no-posts {
    margin-top: 32px;
    text-align: left;
    font-size: 1.125rem;
  }
  .news-post {
    margin-top: 64px;
    padding-bottom: 64px;
  }
  .news-body {
    margin-top: 24px;
  }
  .news-attachments {
    gap: 3%;
    margin-top: 24px;
  }
  .attachment {
    max-width: 30%;
  }
  .edit-news-button {
    margin-top: 24px;
  }
}
</style>
